import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterRule, PagingRule, SortDir, SortRule } from '@webinex/asky';
import { isEmpty } from 'lodash';
import { TableState } from '@app/ui';

const TABLE_DEFAULT_PAGE_SIZE = 10;

function parseFilterRules(
  params: Record<string, any>,
  defaultFilterRules: FilterRule[] | null,
): FilterRule[] {
  return params.filters ? JSON.parse(params.filters as string) : defaultFilterRules ?? [];
}

function parseSortRule(params: Record<string, any>, defaultSortRule: SortRule | null): SortRule | null {
  const [fieldId, dir] = (params.sort as string)?.split(',') ?? [];
  const sortDir: SortDir | null = dir === 'asc' || dir === 'desc' ? dir : null;

  return fieldId && sortDir ? { fieldId, dir: sortDir } : defaultSortRule;
}

function parsePagingRule(params: Record<string, any>, defaultPagingRule: PagingRule): PagingRule {
  const page = parseInt(params.page as string, TABLE_DEFAULT_PAGE_SIZE) || 1;
  const pageSize =
    parseInt(params.pageSize as string, TABLE_DEFAULT_PAGE_SIZE) ||
    defaultPagingRule.take ||
    TABLE_DEFAULT_PAGE_SIZE;
  const skip = (page - 1) * pageSize;

  return { skip, take: pageSize };
}

function createUrlParams(state: Partial<TableState>, searchValue: string | null): Record<string, any> {
  return {
    filters: state.filterRules && !isEmpty(state.filterRules) ? JSON.stringify(state.filterRules) : null,
    sort: state.sortRule ? `${state.sortRule.fieldId},${state.sortRule.dir}` : null,
    page: state.pagingRule ? state.pagingRule.skip / state.pagingRule.take + 1 : 1,
    pageSize: state.pagingRule?.take,
    search: searchValue,
  };
}

function updateUrlParams(params: Record<string, any>) {
  const url = qs.stringify(params, { skipNulls: true });
  window.history.replaceState(null, '', `?${url}`);
}

export function useUrlTableState(uid: string, defaultState: Partial<TableState>) {
  const location = useLocation();
  const [tableState, setTableState] = useState<Partial<TableState>>(defaultState);
  const [searchValue, onSearchChange] = useState<string | null>(null);

  function parseScopedParams(params: Record<string, any>): Record<string, any> {
    const scopedParams = params[uid] ? JSON.parse(params[uid]) : {};
    return scopedParams;
  }

  function createScopedParams(
    tableState: Partial<TableState>,
    searchValue: string | null,
  ): Record<string, any> {
    return {
      [uid]: JSON.stringify(createUrlParams(tableState, searchValue)),
    };
  }

  const resetPage = useCallback(() => {
    setTableState((prev) => {
      const currentPagingRule = prev.pagingRule;
      if (!currentPagingRule) {
        return prev;
      }
      const newPagingRule = { ...currentPagingRule, skip: 0 };
      return { ...tableState, pagingRule: newPagingRule };
    });
  }, [setTableState, tableState]);

  const handleSearchChange = useCallback(
    (value: string | null) => {
      resetPage();
      onSearchChange(value);
    },
    [onSearchChange, resetPage],
  );

  // Initialize state from URL on first render
  useEffect(() => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const scopedParams = parseScopedParams(params);

    const filterRules = parseFilterRules(scopedParams, defaultState.filterRules ?? null);
    const sortRule = parseSortRule(scopedParams, defaultState.sortRule ?? null);
    const pagingRule = parsePagingRule(
      scopedParams,
      defaultState.pagingRule ?? { skip: 0, take: TABLE_DEFAULT_PAGE_SIZE },
    );
    const search = scopedParams.search ?? null;

    setTableState({ filterRules, sortRule, pagingRule });
    onSearchChange(search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Update URL whenever state changes
  useEffect(() => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    const scopedParams = createScopedParams(tableState, searchValue);
    const updatedParams = { ...params, ...scopedParams };

    updateUrlParams(updatedParams);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, searchValue, uid]);

  return [tableState, setTableState, searchValue, handleSearchChange] as const;
}
